import type { ChecksSummaryDetail } from "../data/api/buildStatuses";

/** A checks summary that has something to say — every state but `none`. */
export type ChecksState = "failing" | "passing" | "pending";

/** How the rollup is named wherever it is shown. */
export const CHECKS_LABEL: Record<ChecksState, string> = {
  failing: "checks failing",
  passing: "checks passing",
  pending: "checks running",
};

// Red / amber / green, the vocabulary every CI surface already speaks. A dot
// carries no text at all, so the three have to be told apart on colour alone —
// which `--needs-input` (orange) and `--pending` (amber) are not.
const COLOR: Record<ChecksState, string> = {
  failing: "var(--failed)",
  passing: "var(--done)",
  pending: "var(--pending)",
};

/** CSS custom-property reference for a checks state, e.g. "var(--done)". */
export const checksColorVar = (state: ChecksState): string => COLOR[state];

/**
 * What the rollup says in full, for a surface that shows only a colour.
 *
 * The state, then the buckets behind it worst-first — a red check among green
 * ones is the whole reason to look. When everything landed in one bucket it is
 * counted instead of repeated ("checks passing · 3 checks"), since the state
 * has already named it.
 */
export const checksTooltip = (
  state: ChecksState,
  summary: ChecksSummaryDetail,
): string => {
  const buckets = (
    [
      [summary.failing, "failing"],
      [summary.pending, "pending"],
      [summary.passing, "passing"],
    ] as const
  ).filter(([n]) => n > 0);
  const detail =
    buckets.length > 1
      ? buckets.map(([n, name]) => `${n} ${name}`).join(" · ")
      : `${summary.required_total} check${summary.required_total === 1 ? "" : "s"}`;
  return `${CHECKS_LABEL[state]} · ${detail}`;
};
